// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useCallback, useEffect, useState } from 'react';
import { Badge, Button, DataTable, Panel, StatusBanner } from '@loa/ui-components';
import type { DataTableColumn } from '@loa/ui-components';
import { NewBusinessTemplateEditor } from '@loa/new-business-form';
import { useAdvisor } from '../contexts/AdvisorContext';
import {
  newBusinessTemplateApi, newBusinessTemplateEditorApi, type NewBusinessTemplate,
} from '../api/newBusinessTemplateApi';
import { NewBusinessTemplateViewModal } from './newbusiness/NewBusinessTemplateViewModal';

type Props = Readonly<{ providerId: string }>;

/**
 * "New Business Templates" card on the Provider Detail → Settings tab. Lists the
 * platform-default application-form templates this brokerage inherits (badged
 * "Platform"), collapsed per product by any brokerage override (mirrors
 * resolveActive). A brokerage ADMIN can author brokerage overrides here — the
 * same shared editor the platform admin uses (@loa/new-business-form), minus the
 * raw JSON editors — by overriding a platform default, then uploading their own
 * PDF, placing bindings and activating. See
 * docs/specs/new-business-application-prd.md §5.7.
 */
export function NewBusinessTemplatesCard({ providerId }: Props) {
  const { isAdmin } = useAdvisor();
  const [templates, setTemplates] = useState<NewBusinessTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [viewing, setViewing] = useState<NewBusinessTemplate | null>(null);
  const [editing, setEditing] = useState<NewBusinessTemplate | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [platform, own] = await Promise.all([
        newBusinessTemplateApi.listPlatform(providerId),
        newBusinessTemplateApi.list(providerId),
      ]);
      // Show the effective template per product: a brokerage's own template
      // overrides the platform default (mirrors resolveActive), so the platform
      // row is hidden for any product the brokerage has overridden.
      const byProduct = new Map<string, NewBusinessTemplate>();
      for (const t of platform) byProduct.set(t.productId, t);
      for (const t of own) byProduct.set(t.productId, t);
      setTemplates([...byProduct.values()]);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, [providerId]);

  useEffect(() => { void load(); }, [load]);

  const isPlatform = (t: NewBusinessTemplate) => t.scope === 'PLATFORM';

  // Create a brokerage override from a platform default and open the editor.
  const overrideTemplate = async (platform: NewBusinessTemplate) => {
    setBusy(true);
    setError(null);
    try {
      const created = await newBusinessTemplateApi.create({
        providerId,
        productId: platform.productId,
        currencyCode: platform.currencyCode ?? 'EUR',
        category: platform.category,
        productName: platform.productName,
        providerName: platform.providerName,
      });
      await load();
      setEditing(created);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create an override');
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async (t: NewBusinessTemplate) => {
    setError(null);
    try {
      await (t.isActive ? newBusinessTemplateApi.deactivate(t.id!) : newBusinessTemplateApi.activate(t.id!));
      await load();
    } catch (e) {
      // The publish gate (≥1 signature region + PDF) surfaces here as a 422.
      setError(e instanceof Error ? e.message : 'A PDF and at least one signature region are required to activate.');
    }
  };

  const removeTemplate = async (t: NewBusinessTemplate) => {
    setError(null);
    try {
      await newBusinessTemplateApi.remove(t.id!);
      if (editing?.id === t.id) setEditing(null);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to delete the template');
    }
  };

  const columns: DataTableColumn<NewBusinessTemplate>[] = [
    { key: 'product', header: 'Product', render: (t) => t.productName ?? '—' },
    {
      key: 'source',
      header: 'Source',
      width: '120px',
      render: (t) =>
        isPlatform(t) ? (
          <Badge color="blue" variant="outlined" title="Maintained platform default — read-only">
            Platform
          </Badge>
        ) : (
          <Badge color="purple" variant="filled">Yours</Badge>
        ),
    },
    { key: 'category', header: 'Category', render: (t) => t.category ?? '—' },
    {
      key: 'pdf',
      header: 'Form',
      width: '96px',
      render: (t) =>
        t.hasPdf ? <Badge color="green" variant="filled">PDF</Badge> : <Badge color="grey" variant="outlined">—</Badge>,
    },
    {
      key: 'signatures',
      header: 'Signatures',
      width: '110px',
      render: (t) => String(t.signatureRegionCount ?? 0),
    },
    {
      key: 'status',
      header: 'Status',
      width: '120px',
      render: (t) => (
        <Badge color={t.isActive ? 'green' : 'grey'} variant={t.isActive ? 'filled' : 'outlined'}>
          {t.isActive ? 'Active' : 'Inactive'}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header: '',
      width: isAdmin ? '260px' : '100px',
      render: (t) => (
        <div style={{ display: 'flex', gap: 'var(--spacing-xs)', flexWrap: 'wrap' }}>
          <Button variant="outlined" size="sm" onClick={() => setViewing(t)}>View</Button>
          {isAdmin && isPlatform(t) && (
            <Button
              variant="outlined"
              colour="purple"
              size="sm"
              disabled={busy}
              onClick={() => overrideTemplate(t)}
              data-testid={`nb-override-${t.productId}`}
            >
              Override
            </Button>
          )}
          {isAdmin && !isPlatform(t) && (
            <>
              <Button variant="outlined" size="sm" onClick={() => setEditing(t)} data-testid={`nb-edit-${t.id}`}>
                Edit
              </Button>
              <Button
                variant="outlined"
                colour={t.isActive ? 'red' : 'green'}
                size="sm"
                onClick={() => toggleActive(t)}
                data-testid={`nb-toggle-${t.id}`}
              >
                {t.isActive ? 'Deactivate' : 'Activate'}
              </Button>
              <Button
                variant="outlined"
                colour="red"
                size="sm"
                onClick={() => removeTemplate(t)}
                data-testid={`nb-delete-${t.id}`}
              >
                Delete
              </Button>
            </>
          )}
        </div>
      ),
    },
  ];

  return (
    <Panel
      title="New Business Templates"
      description="Provider application-form templates used to generate new-business applications for this brokerage."
      data-testid="new-business-templates-card"
    >
      {error && <StatusBanner variant="error" message={error} visible position="inline" dismissible={false} />}

      <DataTable<NewBusinessTemplate>
        columns={columns}
        data={templates}
        rowKey={(t) => t.id!}
        loading={loading}
        emptyState="No New Business templates configured for this provider yet."
      />

      {isAdmin && editing && (
        <div style={{ marginTop: 'var(--spacing-lg)', borderTop: '1px solid var(--color-grey-04)', paddingTop: 'var(--spacing-lg)' }}>
          <NewBusinessTemplateEditor
            template={editing}
            api={newBusinessTemplateEditorApi}
            showJsonEditors={false}
            onSaved={() => { void load(); }}
            onClose={() => setEditing(null)}
          />
        </div>
      )}

      {viewing && (
        <NewBusinessTemplateViewModal open template={viewing} onClose={() => setViewing(null)} />
      )}
    </Panel>
  );
}
